import * as React from "react";
import { Autocomplete, TextField } from "@mui/material";
import "./inputCheckBox.css";
import SearchFilterIcon from "../../assets/svgs/searchFilter.svg";
import { OptionsEngenheiro } from "../../utils/optionsAutocomplete";
import { CustomCheckbox, CustomCheckIcon } from "../customCheckbox/customCheckbox";

type Option = OptionsEngenheiro;

interface AutocompleteProps {
  value: OptionsEngenheiro[];
  onChange: (
    event: React.SyntheticEvent,
    newValue: OptionsEngenheiro[]
  ) => void;

  options: OptionsEngenheiro[];
  placeholder?: string;

  inputValue: string;
  setInputValue: (v: string) => void;

  onSearchEngenheiros?: (value: string) => void;
  isReadOnly?: boolean;

  clearState: () => void;
}

const InputCheckBoxEngenheiros = ({
  value,
  onChange,
  placeholder,
  options,
  setInputValue,
  inputValue,
  onSearchEngenheiros,
  isReadOnly = false,
  clearState,
}: AutocompleteProps) => {
  const [open, setOpen] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<Option[]>(
    Array.isArray(value) ? value : []
  );
  const [apiOptions, setApiOptions] = React.useState<Option[]>([]);

  const chipContainerRef = React.useRef<HTMLDivElement>(null);
  const suppressOpenRef = React.useRef(false);

  React.useEffect(() => {
    setSelectedOptions(Array.isArray(value) ? value : []);
  }, [value]);

  React.useEffect(() => {
    if (chipContainerRef?.current) {
      chipContainerRef.current.scrollTo({
        left: chipContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  }, [selectedOptions]);

  React.useEffect(() => {
    setApiOptions(options);
  }, [options]);

  const extractLastToken = (raw: string) => {
    if (!raw) return "";

    const candidate = raw.split(",").pop()?.trim() ?? "";

    return candidate.replaceAll(",", "").replaceAll(";", "").trim();
  };

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: Option[],
    reason: any
  ) => {
    if (reason === "clear") {
      setInputValue("");
      setSelectedOptions([]);
      clearState();
      setOpen(false);
      suppressOpenRef.current = true;
      return;
    }

    setSelectedOptions(newValue);
    onChange(event, newValue);

    setInputValue("");
    suppressOpenRef.current = false;
  };

  const shouldBeBlack = selectedOptions?.length > 0;

  const inputMinHeight = selectedOptions.length > 0 ? 42 : 36;

  const removeOptionByValue = (value: string) => {
    const filteredOptions = (selectedOptions ?? []).filter(
      option => option.value !== value
    );

    setSelectedOptions(filteredOptions);
    onChange(undefined as any, filteredOptions);
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={apiOptions}
        value={selectedOptions}
        onChange={handleChange}
        popupIcon={null}
        open={open && inputValue.length >= 2 && apiOptions.length > 0}
        onOpen={() => {
          if (!inputValue || inputValue.length < 2) return;
          setOpen(true);
        }}
        onClose={() => {
          suppressOpenRef.current = true;
          setOpen(false);
        }}
        inputValue={isReadOnly ? "" : inputValue}
        onInputChange={(_, newInputValue, reason) => {
          if (isReadOnly) return;
          if (reason !== "input") return;

          const lastTyped = extractLastToken(newInputValue);
          setInputValue(lastTyped);

          if (lastTyped.length >= 2 && onSearchEngenheiros) {
            onSearchEngenheiros(lastTyped);
            setOpen(false);
          } else {
            setApiOptions([]);
            setOpen(false);
          }
        }}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        getOptionLabel={(opt) => opt.value}
        filterOptions={(opts) => opts}
        renderOption={(props, option, { selected }) => (
          <li {...props} className="sizeList">
            <CustomCheckbox
              icon={
                <div style={{ width: 24, height: 24, background: "#71767C" }} />
              }
              checkedIcon={<CustomCheckIcon />}
              checked={selected}
            />
            {option.value}
          </li>
        )}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            placeholder={selectedOptions?.length === 0 ? placeholder : ""}
            InputProps={{
              ...params.InputProps,
              readOnly: isReadOnly,
              startAdornment: (
                <div
                  ref={chipContainerRef}
                  style={{
                    maxWidth: "100%",
                    display: "flex",
                    alignItems: "center",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    paddingBottom: "2px",
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  {(selectedOptions || []).map((opt) => (
                    <div
                      key={opt.value}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "#e0e0e0",
                        padding: "2px 6px",
                        borderRadius: "12px",
                        marginRight: "4px",
                        fontSize: "13px",
                        flexShrink: 0,
                      }}
                    >
                      {opt.value}
                      <button
                        style={{
                          marginLeft: "6px",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeOptionByValue(opt.value);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                display: "flex",
                alignItems: "center",
                minHeight: inputMinHeight,
                transition: "min-height 0.2s",
              },
              "& .MuiInputBase-input": {
                paddingLeft: "4px",
              },
              "& .MuiFilledInput-root:before": {
                borderBottom: "1px solid black !important",
              },
              "& .MuiFilledInput-root:after": {
                borderBottom: "1px solid black !important",
              },
              "& .MuiInputBase-input::placeholder": {
                color: shouldBeBlack ? "black" : "grey",
                opacity: shouldBeBlack ? 1 : 0.6,
                fontSize: shouldBeBlack ? 15 : undefined,
              },
            }}
          />
        )}
      />

      <div
        style={{
          position: "absolute",
          right: "11.5px",
          top: "45%",
          transform: "translateY(-20.5%)",
          pointerEvents: "none",
        }}
      >
        <img
          style={{ marginLeft: "5px", width: "18px" }}
          className="iconMessage"
          src={SearchFilterIcon}
          alt="icon-alert"
        />
      </div>
    </div>
  );
};

export default InputCheckBoxEngenheiros;
